import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import AddIcon from '@mui/icons-material/Add';
import CancelSharpIcon from '@mui/icons-material/CancelSharp';
import DeleteIcon from '@mui/icons-material/Delete';
import { Modal, Box, InputAdornment, IconButton, CircularProgress, Button } from '@mui/material';
import reviewConstants from '../../constants/review.constants';
import {
  StyledButton,
  StyledButtonContainer,
  StyledModalBody,
  StyledModalTitle,
  StyledTextField,
} from './ReviewRecordLinksModal.styles';

export default function ReviewRecordLinksModal({ links, isOpen, onSave, onClose, isSaveLoading }) {
  const [linkList, setLinkList] = useState([]);

  useEffect(() => {
    if (isOpen) {
      const initialLinks = links && links.length > 0 ? [...links] : [''];
      setLinkList(
        initialLinks.map((link, index) => ({
          id: `link-${Date.now()}-${index}`,
          value: link,
        })),
      );
    }
  }, [isOpen, links]);

  const handleLinkInput = (index, value) => {
    const newLinks = [...linkList];
    newLinks[index] = { ...newLinks[index], value };
    setLinkList(newLinks);
  };

  const addNewLink = () => {
    const newLink = {
      id: `link-${Date.now()}-${linkList.length}`,
      value: '',
    };
    setLinkList([...linkList, newLink]);
  };

  const removeLink = (index) => {
    if (linkList.length > 1) {
      const newLinks = linkList.filter((_, i) => i !== index);
      setLinkList(newLinks);
    }
  };

  const clearLink = (index) => {
    const newLinks = [...linkList];
    newLinks[index] = { ...newLinks[index], value: '' };
    setLinkList(newLinks);
  };

  const validateUrl = (url) => {
    if (!url.trim()) return true; // Empty is valid
    try {
      const urlObj = new URL(url);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const handleSave = () => {
    const validLinks = linkList
      .map((linkObj) => linkObj.value.trim())
      .filter((link) => link.length > 0)
      .filter((link) => validateUrl(link));

    onSave(validLinks);
  };

  const handleClose = () => {
    setLinkList([]);
    onClose();
  };

  const isSaveDisabled =
    linkList.every((linkObj) => !linkObj.value.trim()) ||
    linkList.some((linkObj) => linkObj.value.trim() && !validateUrl(linkObj.value));

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <StyledModalBody sx={{ width: { xs: '90%', sm: '70%', md: '50%' } }}>
        <Box>
          <StyledModalTitle variant="h6" component="h4">
            Review Records
          </StyledModalTitle>
          <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
            {linkList.map((linkObj, index) => (
              <Box key={linkObj.id} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <StyledTextField
                  sx={{ flexGrow: 1, mr: 1 }}
                  value={linkObj.value}
                  variant="outlined"
                  onChange={(e) => handleLinkInput(index, e.target.value)}
                  label={`Link ${index + 1}`}
                  error={linkObj.value.trim() && !validateUrl(linkObj.value)}
                  helperText={
                    linkObj.value.trim() && !validateUrl(linkObj.value)
                      ? reviewConstants.ERROR_MESSAGES.URL_IS_INVALID
                      : ''
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => clearLink(index)}
                          size="small"
                          disabled={!linkObj.value.trim()}
                        >
                          <CancelSharpIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                {linkList.length > 1 && (
                  <IconButton
                    onClick={() => removeLink(index)}
                    size="small"
                    color="error"
                    sx={{ ml: 1 }}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={addNewLink} sx={{ mb: 2 }}>
              Add Link
            </Button>
          </Box>
        </Box>
        <StyledButtonContainer>
          <StyledButton
            onClick={handleSave}
            variant="contained"
            disabled={isSaveDisabled || isSaveLoading}
          >
            {isSaveLoading ? <CircularProgress size={24} /> : 'Save'}
          </StyledButton>
        </StyledButtonContainer>
      </StyledModalBody>
    </Modal>
  );
}

ReviewRecordLinksModal.propTypes = {
  links: PropTypes.arrayOf(PropTypes.string),
  isOpen: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  isSaveLoading: PropTypes.bool,
};

ReviewRecordLinksModal.defaultProps = {
  links: [],
  isSaveLoading: false,
};
